import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@common/contexts/ToastContext';
import { isTimeoutError } from '@common/utils/apiError';

/**
 * Hook for handling async actions with loading state and toast notifications
 * Provides consistent error handling across the application
 *
 * @param {Object} options - Configuration options
 * @param {string} options.errorTitle - i18n key for error toast title
 * @param {string} options.timeoutMessage - i18n key for timeout error message
 * @param {string} options.defaultErrorMessage - i18n key for default error message
 * @param {string} options.successTitle - i18n key for success toast title (optional)
 * @param {string} options.successMessage - i18n key for success toast message (optional)
 * @returns {Object} - { execute, loading }
 */
export function useAsyncAction(options = {}) {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const [loading, setLoading] = useState(false);

  const {
    errorTitle = 'common.error',
    timeoutMessage = 'common.errors.timeout',
    defaultErrorMessage = 'common.errors.general',
    successTitle,
    successMessage,
  } = options;

  const execute = useCallback(
    async (asyncFn) => {
      setLoading(true);
      try {
        const result = await asyncFn();

        // Show success toast if configured
        if (successTitle && successMessage) {
          showSuccess(t(successTitle), t(successMessage));
        }

        return { success: true, data: result };
      } catch (error) {
        // Determine error message based on error type
        let message;
        if (isTimeoutError(error)) {
          message = t(timeoutMessage);
        } else if (error.message) {
          message = error.message;
        } else {
          message = t(defaultErrorMessage);
        }

        showError(t(errorTitle), message);

        return { success: false, error };
      } finally {
        setLoading(false);
      }
    },
    [t, showError, showSuccess, errorTitle, timeoutMessage, defaultErrorMessage, successTitle, successMessage]
  );

  return { execute, loading };
}

/**
 * Pre-configured hook for export operations
 * Uses export-specific error messages
 *
 * @returns {Object} - { execute, loading }
 */
export function useExportAction() {
  return useAsyncAction({
    errorTitle: 'dashboard.exportError',
    timeoutMessage: 'dashboard.exportTimeoutMessage',
    defaultErrorMessage: 'dashboard.exportFailedMessage',
  });
}

export default useAsyncAction;

